import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import androidx.lifecycle.ViewModelProvider
import androidx.lifecycle.ViewModelStore
import com.mmk.kmpnotifier.KMPNotifier
import com.mmk.kmpnotifier.local.LocalNotifications
import com.mmk.kmpnotifier.notification.configuration.NotificationPlatformConfiguration
import dev.zacsweers.metro.createGraph
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import org.jetbrains.skiko.wasm.onWasmReady
import org.maplibre.compose.browser.installMaplibreCompose
import com.bikompanion.data.analytics.configureTelemetry
import com.bikompanion.data.appVersionName
import com.bikompanion.data.isDebugBuild
import com.bikompanion.ui.home.HomeScreen
import com.bikompanion.ui.home.HomeScreenActions
import com.bikompanion.ui.home.HomeViewModel
import com.bikompanion.ui.settings.SettingsPlatform
import com.bikompanion.ui.watch.StationWatchEvent
import com.bikompanion.ui.watch.StationWatchNotificationListener
import com.bikompanion.ui.watch.StationWatchNotifications
import com.bikompanion.ui.watch.requestStationWatchPermission

@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    configureTelemetry(enabled = !isDebugBuild)
    onWasmReady {
        KMPNotifier.initialize(
            NotificationPlatformConfiguration.Web(
                askNotificationPermissionOnStart = false,
                notificationIconPath = null,
            ),
            LocalNotifications,
        )
        // No `workerUrl`: since 0.20 the worker ships inside the library and is started from an
        // inline blob, so there is no separate worker file to serve.
        installMaplibreCompose()
        val appGraph = createGraph<AppGraph>()
        val homeViewModel = ViewModelProvider.create(ViewModelStore(), appGraph.metroViewModelFactory)[HomeViewModel::class]
        StationWatchNotificationListener { homeViewModel.onAction(HomeScreenActions.StationNotificationSelected(it)) }
        document.addEventListener("visibilitychange", {
            if (document.asDynamic().hidden == false) homeViewModel.onAction(HomeScreenActions.WatchedStationsRefreshRequested)
        })
        val stationHistory = BrowserStationHistory(homeViewModel, MainScope())
        stationHistory.start()
        stationHistory.openCurrentAddress()
        ComposeViewport(document.body!!) {
            LaunchedEffect(Unit) {
                // One frame in, so the map's first draw replaces the splash rather than a blank page.
                withFrameNanos { }
                document.getElementById("splash")?.remove()
            }
            HomeScreen(
                viewModel = homeViewModel,
                onShareLink = ::shareInBrowser,
                shareBaseUrl = browserBaseUrl,
                settingsPlatform = SettingsPlatform(
                    versionName = appVersionName,
                    notifications = browserNotificationControls(),
                    loadLicenses = ::loadBrowserLicenses,
                    loadInstallId = appGraph.anonymousIdentity::currentId,
                ),
                appInstallOffer = rememberBrowserInstallOffer(),
                showGlassPlayground = false,
                showDebugNotificationButton = isDebugBuild,
                onDebugNotificationClick = {
                    homeViewModel.onAction(HomeScreenActions.DebugFoundNotificationRequested)
                },
                requestWatchPermission = ::requestStationWatchPermission,
                onStationWatchEvent = { event ->
                    when (event) {
                        is StationWatchEvent.Found -> StationWatchNotifications.postFound(event.watches)
                        is StationWatchEvent.Expired -> StationWatchNotifications.postExpired(event.watches)
                        is StationWatchEvent.Started, StationWatchEvent.Stopped -> Unit
                    }
                },
            )
        }
    }
}
